import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  accountQuery,
  buildCasinoGameFilters,
  casinoGamesInfiniteQuery,
  casinoVendorsQuery,
  useAuthSession,
} from '@/api';
import {
  CASINO_PAGE_SIZE_DESKTOP,
  CASINO_PAGE_SIZE_MOBILE,
  casinoNavSlug,
  isCasinoFlagSlug,
} from '@/domain';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { CasinoChrome } from './CasinoChrome';
import { GameGrid } from './GameGrid';
import { SelectedProviderBar } from './SelectedProviderBar';
import { useCasinoFavorites } from './useCasinoFavorites';
import { useCasinoInfiniteScroll } from './useCasinoInfiniteScroll';

export interface CasinoCategoryPageProps {
  category: string;
  vendorOrFlag?: string;
  liveMode?: boolean;
  title?: string;
  initialSearch?: string;
}

function navIdForCategory(category: string): string {
  const slug = casinoNavSlug(category);
  if (slug === 'hot' || slug === 'popular') return 'hot';
  if (slug === 'favorites') return 'favorites';
  if (slug === 'livecasino') return 'livecasino';
  if (slug === 'slots') return 'slots';
  if (slug === 'tablegames') return 'tablegames';
  if (slug === 'gameshows') return 'gameshows';
  if (slug === 'blackjack') return 'blackjack';
  if (slug === 'roulette') return 'roulette';
  if (slug === 'baccarat') return 'baccarat';
  return slug || 'slots';
}

function titleFor(category: string, vendorOrFlag?: string): string {
  if (vendorOrFlag && !isCasinoFlagSlug(vendorOrFlag) && casinoNavSlug(vendorOrFlag) !== 'popular') {
    return vendorOrFlag;
  }
  const slug = casinoNavSlug(category);
  const map: Record<string, string> = {
    hot: 'Hot Games',
    slots: 'Slots',
    livecasino: 'Live Casino',
    tablegames: 'Table Games',
    gameshows: 'Game Shows',
    favorites: 'Favorites',
    blackjack: 'Blackjack',
    roulette: 'Roulette',
    baccarat: 'Baccarat',
    poker: 'Poker',
  };
  if (vendorOrFlag && isCasinoFlagSlug(vendorOrFlag)) {
    return `${map[slug] ?? category} · ${vendorOrFlag}`;
  }
  if (vendorOrFlag && casinoNavSlug(vendorOrFlag) === 'popular') {
    return 'Popular';
  }
  return map[slug] ?? category;
}

export function CasinoCategoryPage({
  category,
  vendorOrFlag,
  liveMode = false,
  title,
  initialSearch = '',
}: CasinoCategoryPageProps) {
  const isDesktop = useIsDesktop();
  const { user } = useAuthSession();
  const { data: account } = useQuery(accountQuery());
  const { showFavorite, toggleFavorite } = useCasinoFavorites();
  const [search, setSearch] = useState(initialSearch);

  const categorySlug = casinoNavSlug(category);
  const isFavorites = categorySlug === 'favorites';
  const isHot = categorySlug === 'hot';
  const isVendorFilter =
    Boolean(vendorOrFlag) &&
    !isCasinoFlagSlug(vendorOrFlag) &&
    casinoNavSlug(vendorOrFlag) !== 'popular';

  const vendorLookupFilters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category: isHot ? 'slots' : categorySlug,
        liveMode: liveMode || categorySlug === 'livecasino',
        popular: isHot || undefined,
        favorites: isFavorites || undefined,
      }),
    [account, user, categorySlug, liveMode, isHot, isFavorites],
  );
  const vendorsQuery = useQuery({
    ...casinoVendorsQuery(vendorLookupFilters),
    enabled: isVendorFilter && Boolean(vendorLookupFilters.AccountGuid),
  });

  const resolvedVendorName = useMemo(() => {
    if (!isVendorFilter || !vendorOrFlag) return null;
    const slug = casinoNavSlug(vendorOrFlag);
    const match = (vendorsQuery.data ?? []).find(
      (vendor) => casinoNavSlug(vendor.Name) === slug,
    );
    return match?.Name ?? vendorOrFlag;
  }, [isVendorFilter, vendorOrFlag, vendorsQuery.data]);

  const selectedVendor = resolvedVendorName;

  const filters = useMemo(
    () =>
      buildCasinoGameFilters({
        account,
        user,
        category: isHot ? 'slots' : categorySlug,
        vendorOrFlag: isVendorFilter ? resolvedVendorName : vendorOrFlag,
        searchText: search,
        liveMode: liveMode || categorySlug === 'livecasino',
        popular: isHot || undefined,
        favorites: isFavorites || undefined,
      }),
    [
      account,
      user,
      categorySlug,
      vendorOrFlag,
      resolvedVendorName,
      isVendorFilter,
      search,
      liveMode,
      isHot,
      isFavorites,
    ],
  );

  const pageSize = isDesktop ? CASINO_PAGE_SIZE_DESKTOP : CASINO_PAGE_SIZE_MOBILE;
  const gamesQuery = useInfiniteQuery(casinoGamesInfiniteQuery(filters, pageSize));

  const games = useMemo(
    () => gamesQuery.data?.pages.flatMap((page) => page.Data) ?? [],
    [gamesQuery.data],
  );

  const sentinelRef = useCasinoInfiniteScroll(
    true,
    Boolean(gamesQuery.hasNextPage),
    gamesQuery.isFetchingNextPage,
    () => {
      void gamesQuery.fetchNextPage();
    },
  );

  return (
    <CasinoChrome
      activeNavId={navIdForCategory(category)}
      searchValue={search}
      onSearchCommit={setSearch}
      showCategoryNav={!selectedVendor}
    >
      {selectedVendor ? (
        <SelectedProviderBar vendorName={selectedVendor} category={categorySlug || 'slots'} />
      ) : null}
      <GameGrid
        title={title ?? titleFor(category, vendorOrFlag)}
        games={games}
        isLoading={gamesQuery.isLoading}
        isFetchingMore={gamesQuery.isFetchingNextPage}
        categoryOverride={
          liveMode || categorySlug === 'livecasino' ? 'livecasino' : categorySlug || undefined
        }
        showFavorite={showFavorite}
        onToggleFavorite={toggleFavorite}
        sentinelRef={sentinelRef}
      />
    </CasinoChrome>
  );
}
